import React from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const PIE_COLORS = ["#006881", "#3FC4E3", "#0e7490", "#7dd3c0", "#b8860b", "#94a3b8"];

const sum = (arr, key) => arr.reduce((acc, item) => acc + (item[key] || 0), 0);

const ImpactChart = ({ data }) => {
  const programType = data?.requestsByProgramType || [];
  const growth = data?.programGrowth || [];
  const income = data?.incomeLevelSupport || [];
  const zip = data?.impactByZip || [];

  const programTypeCurrent = sum(programType, "value");
  const programTypePrev = sum(programType, "previous");
  const programTypeDelta =
    programTypePrev === 0
      ? programTypeCurrent > 0
        ? 100
        : 0
      : Math.round(((programTypeCurrent - programTypePrev) / programTypePrev) * 1000) / 10;

  const growthTotal = sum(growth, "value");
  const zipTotal = sum(zip, "value");
  const incomeTotal = sum(income, "value");

  const DeltaBadge = ({ value }) => (
    <span className={value >= 0 ? "text-[#08873B]" : "text-[#E83808]"}>
      {value >= 0 ? "+" : ""}
      {value}%
    </span>
  );

  const EmptyNote = () => (
    <div className="h-[200px] flex items-center justify-center text-sm text-black/40 font-popinsRegular">
      No data yet
    </div>
  );

  return (
    <div className="mt-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Requests by Program Type — current vs previous */}
        <div className="bg-white rounded-xl p-4 shadow border border-border">
          <h3 className="text-lg font-popinsSemiBold">Requests by Program Type</h3>
          <p className="text-3xl font-popinsBold py-3">{programTypeCurrent}</p>
          <p className="text-sm text-black/50 mb-2 font-popinsRegular">
            This Month <DeltaBadge value={programTypeDelta} />
          </p>
          {programType.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={programType}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar name="Previous" dataKey="previous" fill="#cbd5e1" barSize={16} radius={[5, 5, 0, 0]} />
                <Bar name="Current" dataKey="value" fill="#06b6d4" barSize={16} radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyNote />
          )}
        </div>

        {/* Program Growth Over Time */}
        <div className="bg-white rounded-xl p-4 shadow border border-border">
          <h3 className="text-lg font-popinsSemiBold">Program Growth Over Time</h3>
          <p className="text-3xl font-popinsBold py-3">{growthTotal}</p>
          <p className="text-sm text-black/50 mb-2 font-popinsRegular">Last 6 Months</p>
          {growth.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={growth}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis hide />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#06b6d4" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyNote />
          )}
        </div>

        {/* Income Level Support */}
        <div className="bg-white rounded-xl p-4 shadow border border-border">
          <h3 className="text-lg font-popinsSemiBold">Income Level Support</h3>
          <p className="text-3xl font-popinsBold py-3">{incomeTotal}</p>
          <p className="text-sm text-black/50 mb-2 font-popinsRegular">Inquiries by income level</p>
          {income.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={income}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {income.map((entry, index) => (
                    <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <EmptyNote />
          )}
        </div>
      </div>

      {/* Impact by ZIP Code */}
      <div className="bg-white rounded-xl p-4 shadow border border-border">
        <h3 className="text-lg font-popinsSemiBold">Impact by ZIP Code</h3>
        <p className="text-3xl font-popinsBold py-3">{zipTotal}</p>
        <p className="text-sm text-black/50 mb-2 font-popinsRegular">
          Events &amp; resources by ZIP
        </p>
        {zip.length ? (
          <ResponsiveContainer width="100%" height={Math.max(200, zip.length * 40)}>
            <BarChart
              data={zip}
              layout="vertical"
              margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
            >
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="zip" width={70} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#06b6d4" radius={[0, 5, 5, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyNote />
        )}
      </div>
    </div>
  );
};

export default ImpactChart;
